<template>
  <div class="fund-holdings page-shell">
    <div class="toolbar">
      <span class="toolbar-label">报告期</span>
      <el-select v-model="selectedPeriod" placeholder="选择报告期" size="small" @change="onPeriodChange">
        <el-option
          v-for="item in periodOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <span class="toolbar-label">代码</span>
      <el-input
        v-model="codeFilter"
        size="small"
        clearable
        placeholder="输入股票代码"
        style="width: 160px"
        @input="onFilterChange"
        @clear="onFilterChange"
      />
      <span class="toolbar-label">名称</span>
      <el-input
        v-model="nameFilter"
        size="small"
        clearable
        placeholder="输入证券名称"
        style="width: 160px"
        @input="onFilterChange"
        @clear="onFilterChange"
      />
      <el-button type="primary" size="small" :disabled="!filteredTableData.length" @click="exportExcel">
        导出按家数
      </el-button>
      <el-button type="primary" size="small" plain :disabled="!filteredTableData.length" @click="exportExcelByMarketValue">
        导出按市值
      </el-button>
    </div>

    <div ref="tableShell" class="table-shell" v-loading="tableLoading" element-loading-text="加载中...">
      <VirtualTable
        ref="fundTable"
        :data="filteredTableData"
        :height="tableHeight"
        :row-height="32"
        :default-sort="{ prop: 'shares', order: 'descending' }"
        :row-style="{ height: '32px', lineHeight: '32px' }"
        :cell-style="{ padding: '2px 0' }"
        style="width: 100%"
        @range-change="onVirtualRangeChange"
      >
        <el-table-column label="序号" width="80">
          <template slot-scope="{ $index }">
            {{ rowOffset + $index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="code" label="代码" sortable="custom" width="120">
          <template slot-scope="{ row }">
            <el-link :href="row.codeLink" target="_blank" rel="noopener noreferrer">{{ row.code }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" width="120">
          <template slot-scope="{ row }">
            <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">{{ row.name }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="shares" sortable="custom" label="持有基金家数(家)" />
        <el-table-column prop="shareAmount" label="持股总数(万股)" />
        <el-table-column prop="marketValue" sortable="custom" label="持股市值(亿元)" />
        <el-table-column prop="action" sortable="custom" label="持股变化" />
        <el-table-column prop="changeAmount" label="持股变动数值(万股)" />
        <el-table-column prop="changePercent" label="持股变动比例" />
        <el-table-column prop="fundUrl" label="相关链接">
          <template slot-scope="{ row }">
            <el-link v-if="row.fundUrl" :href="`//fund.stockstar.com${row.fundUrl}`" target="_blank" rel="noopener noreferrer">受益基金</el-link>
            <span v-else style="color: #909399">—</span>
          </template>
        </el-table-column>
      </VirtualTable>
    </div>
  </div>
</template>

<script>
import { fetchFundPeriods, fetchFundHoldings } from '../utils/fundHoldingsApi.js'
import { eastmoneyCodeLink } from '../utils/stockLink.js'
import VirtualTable from './VirtualTable.vue'

// 期数据内存缓存（同一会话内切换报告期不重复请求）
const fundTableCache = new Map()

export default {
  name: 'FundHoldings',
  components: {
    VirtualTable,
  },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedPeriod: '',
      periodOptions: [],
      tableData: [],
      codeFilter: '',
      nameFilter: '',
      tableLoading: false,
      loadToken: 0,
      tableHeight: 560,
      rowOffset: 0,
    }
  },
  computed: {
    filteredTableData() {
      const codeKeyword = String(this.codeFilter || '').trim()
      const nameKeyword = String(this.nameFilter || '').trim()
      if (!codeKeyword && !nameKeyword) return this.tableData
      return this.tableData.filter((row) => {
        const codeOk = !codeKeyword || String(row.code || '').includes(codeKeyword)
        const nameOk = !nameKeyword || String(row.name || '').includes(nameKeyword)
        return codeOk && nameOk
      })
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.updateTableHeight()
      }
    },
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  created() {
    this.initPeriods()
  },
  methods: {
    onVirtualRangeChange({ startIndex }) {
      this.rowOffset = startIndex
    },
    async initPeriods() {
      try {
        const periods = await fetchFundPeriods()
        if (!periods.length) return
        this.periodOptions = periods.map((p) => ({ label: p.period, value: p.period }))
        this.selectedPeriod = this.periodOptions[0].value
        this.loadTableData()
      } catch (err) {
        this.$message?.error(`报告期列表加载失败: ${err.message}`)
      }
    },
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(240, Math.floor(window.innerHeight - top - 20))
        if (height !== this.tableHeight) {
          this.tableHeight = height
        }
        this.$refs.fundTable?.relayout()
      })
    },
    onPeriodChange() {
      this.loadTableData()
    },
    onFilterChange() {
      this.rowOffset = 0
      this.$nextTick(() => {
        this.$refs.fundTable?.relayout()
      })
    },
    async loadTableData() {
      if (!this.selectedPeriod) return
      const token = ++this.loadToken
      const cached = fundTableCache.get(this.selectedPeriod)
      if (cached) {
        this.tableData = cached
        this.updateTableHeight()
        return
      }

      this.tableLoading = true
      try {
        const rows = await fetchFundHoldings(this.selectedPeriod)
        if (token !== this.loadToken) return
        fundTableCache.set(this.selectedPeriod, rows)
        this.tableData = rows
        this.updateTableHeight()
      } catch (err) {
        if (token === this.loadToken) {
          this.$message?.error(`持仓数据加载失败: ${err.message}`)
        }
      } finally {
        if (token === this.loadToken) {
          this.tableLoading = false
        }
      }
    },
    buildExportRows(list) {
      return list.map((item, index) => ({
        序号: index + 1,
        代码: item.code,
        名称: item.name,
        '持有基金家数(家)': item.shares,
        '持股总数(万股)': item.shareAmount,
        '持股市值(亿元)': item.marketValue,
        持股变化: item.action,
        '持股变动数值(万股)': item.changeAmount,
        持股变动比例: item.changePercent,
        受益基金链接: item.fundUrl ? `https://fund.stockstar.com${item.fundUrl}` : '',
      }))
    },
    downloadExcel(rows, filename) {
      return import('xlsx').then((XLSX) => {
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, this.selectedPeriod)
        XLSX.writeFile(workbook, filename)
      })
    },
    exportExcel() {
      this.downloadExcel(
        this.buildExportRows(this.filteredTableData),
        `基金重仓股_${this.selectedPeriod}.xlsx`
      )
    },
    exportExcelByMarketValue() {
      const sorted = [...this.filteredTableData].sort((a, b) => b.marketValue - a.marketValue)
      this.downloadExcel(
        this.buildExportRows(sorted),
        `基金重仓股_按持股市值_${this.selectedPeriod}.xlsx`
      )
    },
    eastmoneyCodeLink,
  },
}
</script>

<style scoped>
.fund-holdings {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  text-align: left;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.table-shell {
  min-height: 0;
}
</style>
